import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import isString from 'lodash/isString.js';
import isArrayLikeObject from 'lodash/isArrayLikeObject.js';
import PendoIcon from '../icon/pendo-icon.vue.js';
import PendoLoadingIndicator from '../loading-indicator/pendo-loading-indicator.vue.js';
import PickerPopover from '../../utils/picker-popover.vue.js';
import Shortcuts from './shortcuts.vue.js';
import Calendar from './calendar.vue.js';
import SinglePicker from './utils/pickers/single.js';
import MultiplePicker from './utils/pickers/multiple.js';
import RangePicker from './utils/pickers/range.js';
import Attribute from './utils/attribute.js';
import Locale from './utils/locale.js';
import locales from './utils/locales.js';
import { addDays } from './utils/date-info.js';
import { setStyles, setAttributes } from '../../utils/dom.js';

const DEFAULT_MASKS = {
  title: 'MMMM YYYY',
  weekdays: 'W',
  months: 'MMM',
  input: ['MMM D, YYYY', 'L', 'YYYY-MM-DD', 'YYYY/MM/DD'],
  data: ['L', 'YYYY-MM-DD', 'YYYY/MM/DD']
};
var script = {
  name: 'PendoDatePicker',
  components: {
    Calendar,
    PendoIcon,
    PendoLoadingIndicator,
    PickerPopover,
    Shortcuts
  },
  provide() {
    return {
      state: this
    };
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      type: null,
      required: true
    },
    type: {
      type: String,
      default: 'single',
      validator: type => ['single', 'multiple', 'range'].includes(type)
    },
    loading: {
      type: Boolean,
      default: false
    },
    isRequired: Boolean,
    disabledDates: {
      type: null,
      default: null
    },
    availableDates: {
      type: null,
      default: null
    },
    updateOnInput: {
      type: Boolean,
      default: false
    },
    updateOnDrag: {
      type: Boolean,
      default: false
    },
    popover: {
      type: Object,
      default: () => ({})
    },
    popperOptions: {
      type: Object,
      default: () => ({})
    },
    labels: {
      type: Object,
      default: () => ({})
    },
    appendToBody: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    minTriggerWidth: {
      type: [String, Number],
      default: 160
    },
    maxTriggerWidth: {
      type: [String, Number],
      default: 400
    },
    maxMenuWidth: {
      type: [String, Number],
      default: 400
    },
    minMenuWidth: {
      type: [String, Number],
      default: 160
    },
    maxMenuHeight: {
      type: [String, Number],
      default: 300
    },
    triggerSize: {
      type: String,
      default: 'medium',
      validator: triggerSize => ['mini', 'small', 'medium'].includes(triggerSize)
    },
    firstDayOfWeek: {
      type: Number,
      default: 1
    },
    masks: {
      type: Object,
      default: null
    },
    localeCode: {
      type: String,
      default: 'en-US'
    },
    columns: {
      type: Number,
      default: 1
    },
    step: {
      type: Number,
      default: 1
    },
    fromDate: Date,
    toDate: Date,
    minDate: {
      type: null,
      default: null
    },
    maxDate: {
      type: null,
      default: null
    },
    shortcuts: {
      type: [Boolean, Array],
      default: () => [{
        label: 'Today',
        value: {
          start: 0,
          end: 0
        }
      }, {
        label: 'Yesterday',
        value: {
          start: -1,
          end: -1
        }
      }, {
        label: 'Last 7 Days',
        value: {
          start: -1,
          end: -7
        }
      }, {
        label: 'Last 30 Days',
        value: {
          start: -1,
          end: -30
        }
      }, {
        label: 'Last 90 Days',
        value: {
          start: -1,
          end: -90
        }
      }]
    },
    placeholder: {
      type: String,
      default: 'Select date'
    },
    editable: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dragValue: null,
      inputValue: null,
      inputFocused: false,
      focused: false,
      isPopoverVisible: false,
      disableFormatInput: false,
      disablePopoverHide: false,
      isCalendarVisible: false,
      visibleColumnCount: this.columns,
      twoColumnBreakpoint: null
    };
  },
  computed: {
    popoverOptions() {
      let behavior = [];
      if (this.popperOptions && this.popperOptions.placement) {
        if (this.popperOptions.placement.includes('-end')) {
          behavior = ['bottom-end', 'top-end'];
        } else {
          behavior = ['bottom-start', 'top-start'];
        }
      }
      return _objectSpread2(_objectSpread2({}, this.popperOptions), {}, {
        modifiers: {
          hide: {
            enabled: false
          },
          preventOverflow: {
            enabled: false
          },
          flip: {
            enabled: true,
            behavior,
            fn: this.flipPopperVariations
          },
          arrow: {
            enabled: false
          },
          applyStyle: {
            enabled: true,
            order: 900,
            fn: this.applyPopperStyle
          }
        }
      });
    },
    model: {
      get() {
        return this.picker.normalize(this.value);
      },
      set(val) {
        this.$emit('change', val);
        if (!this.disableFormatInput) {
          this.formatInput();
        }
        if (this.type !== 'multiple' && this.isPopoverVisible && !this.disablePopoverHide) {
          this.hidePopover();
        }
        this.disableFormatInput = false;
        this.disablePopoverHide = false;
      }
    },
    isDisabled() {
      return this.disabled || this.loading;
    },
    showShortcuts() {
      if (this.type !== 'range') {
        return !!this.$options.propsData.shortcuts;
      }
      return !!this.shortcuts;
    },
    shortcutOptions() {
      if (!this.showShortcuts) {
        return null;
      }
      const shortcuts = this.shortcuts.map(shortcut => {
        const newValue = this.picker.parseShortcutValue(shortcut.value);
        const selected = this.picker.valuesAreEqual(this.model, newValue);
        return _objectSpread2(_objectSpread2({}, shortcut), {}, {
          value: newValue,
          disabled: !this.dateIsValid(newValue),
          selected: !this.isCalendarVisible && selected
        });
      });
      const isCustomRangeSelected = shortcuts.every(option => !option.selected);
      shortcuts.push({
        label: 'Custom Range',
        value: 'custom',
        disabled: false,
        selected: isCustomRangeSelected || this.isCalendarVisible
      });
      return shortcuts;
    },
    locale() {
      const detLocale = new Intl.DateTimeFormat().resolvedOptions().locale;
      let id = this.localeCode || detLocale;
      id = [id, id.substring(0, 2)].find(i => locales[i]) || detLocale;
      const config = {
        id,
        firstDayOfWeek: this.firstDayOfWeek || locales[id].firstDayOfWeek,
        masks: _objectSpread2(_objectSpread2(_objectSpread2({}, locales[id].masks), DEFAULT_MASKS), this.masks)
      };
      return new Locale(config);
    },
    inputMasks() {
      const inputFormat = this.locale.masks.input;
      return isArrayLikeObject(inputFormat) && inputFormat || [inputFormat];
    },
    selectAttribute() {
      if (!this.picker.hasValue(this.model)) {
        return null;
      }
      return {
        key: 'select',
        dates: this.model,
        pinPage: true
      };
    },
    dragAttribute() {
      if (this.type !== 'range' || !this.picker.hasValue(this.dragValue)) {
        return null;
      }
      return {
        key: 'drag',
        dates: this.dragValue
      };
    },
    disabledAttribute() {
      const dates = [];
      if (this.disabledDates) {
        if (isArrayLikeObject(this.disabledDates)) {
          dates.push(...this.disabledDates);
        } else {
          dates.push(this.disabledDates);
        }
      }
      const minDate = this.locale.toDate(this.minDate);
      const maxDate = this.locale.toDate(this.maxDate);
      if (minDate) {
        dates.push({
          start: null,
          end: addDays(minDate, -1)
        });
      }
      if (maxDate) {
        dates.push({
          start: addDays(maxDate, 1),
          end: null
        });
      }
      return new Attribute({
        key: 'disabled',
        dates,
        excludeDates: this.availableDates,
        excludeMode: 'includes'
      }, this.locale);
    },
    activeAttribute() {
      if (this.dragAttribute) {
        return this.dragAttribute;
      }
      if (this.selectAttribute) {
        return this.selectAttribute;
      }
      return null;
    },
    picker() {
      const opts = {
        locale: this.locale,
        format: date => this.locale.format(date, this.inputMasks[0]),
        parse: str => this.locale.parse(str, this.inputMasks)
      };
      switch (this.type) {
        case 'multiple':
          return new MultiplePicker(opts);
        case 'range':
          return new RangePicker(opts);
        case 'date':
        case 'day':
        default:
          return new SinglePicker(opts);
      }
    }
  },
  watch: {
    model() {
      this.formatInput();
    },
    type() {
      this.inputValue = null;
    },
    dragValue(val) {
      if (this.updateOnDrag) {
        this.formatInput();
      }
      this.$emit('drag', this.picker.normalize(val));
    },
    minTriggerWidth() {
      setStyles(this.$refs.trigger, {
        minWidth: this.fullWidth ? '100%' : this.minTriggerWidth
      });
    },
    maxTriggerWidth() {
      setStyles(this.$refs.trigger, {
        maxWidth: this.maxTriggerWidth
      });
    }
  },
  created() {
    this.formatInput();
  },
  mounted() {
    setStyles(this.$refs.trigger, {
      minWidth: this.fullWidth ? '100%' : this.minTriggerWidth,
      maxWidth: this.maxTriggerWidth
    });
  },
  methods: {
    handleShortcutSelect(option) {
      if (option.value === 'custom') {
        this.isCalendarVisible = true;
        return;
      }
      this.$emit('select-shortcut', option);
      this.disableFormatInput = true;
      this.picker.handleShortcutSelect(option, this);
      this.inputValue = option.label;
      this.isPopoverVisible = false;
      this.isCalendarVisible = false;
    },
    dateIsValid(date) {
      return !!this.disabledAttribute && !this.disabledAttribute.intersectsDate(date);
    },
    handleDayClick(day) {
      this.picker.handleDayClick(day, this);
      this.$emit('dayclick', day);
    },
    handleDayFocusIn(day) {
      if (day.el) {
        day.el.blur();
      }
      this.$emit('dayfocusin', day);
    },
    handleDayMouseEnter(day) {
      this.picker.handleDayMouseEnter(day, this);
      this.$emit('daymouseenter', day);
    },
    updateInputValue(_ref) {
      let {
        target
      } = _ref;
      this.inputValue = target.value;
      if (this.updateOnInput) {
        this.updateValue(this.inputValue, {
          formatInput: false,
          hidePopover: false
        });
      }
    },
    handleInputBlur() {
      this.inputFocused = false;
    },
    handleInputFocus() {
      this.inputFocused = true;
      this.showPopover();
    },
    handleInputChange() {
      this.updateValue(this.inputValue, {
        formatInput: true,
        hidePopover: false
      });
    },
    handleInputEsc() {
      this.updateValue(this.model, {
        formatInput: true,
        hidePopover: true
      });
    },
    updateValue(value) {
      let {
        formatInput,
        hidePopover
      } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      this.inputValue = isString(value) ? value : this.inputValue;
      const userValue = isString(value) ? this.picker.parseValue(value) : value;
      const validatedValue = this.picker.filterDisabled({
        value: this.picker.normalize(userValue),
        isRequired: this.isRequired,
        disabled: this.disabledAttribute,
        fallbackValue: this.model
      });
      if (this.picker.valuesAreEqual(this.model, validatedValue)) {
        if (formatInput) {
          this.formatInput();
        }
        if (hidePopover) {
          this.hidePopover();
        }
      } else {
        this.disableFormatInput = !formatInput;
        this.disablePopoverHide = !hidePopover;
        this.model = validatedValue;
      }
    },
    formatInput() {
      this.$nextTick(() => {
        if (this.picker.hasValue(this.dragValue)) {
          this.inputValue = this.picker.formatValue(this.dragValue);
          return;
        }
        const matchingShortcut = this.showShortcuts && this.shortcutOptions.filter(shortcut => shortcut.value !== 'custom').find(shortcut => this.picker.valuesAreEqual(shortcut.value, this.model));
        if (matchingShortcut) {
          this.inputValue = matchingShortcut.label;
          return;
        }
        this.inputValue = this.picker.formatValue(this.model);
      });
    },
    togglePopover() {
      if (this.isDisabled) {
        return;
      }
      if (this.isPopoverVisible) {
        if (this.editable && this.inputFocused) {
          return;
        }
        this.hidePopover();
      } else {
        this.showPopover();
      }
    },
    hidePopover() {
      this.dragValue = null;
      this.isPopoverVisible = false;
      this.isCalendarVisible = false;
    },
    showPopover() {
      if (this.showShortcuts) {
        this.isCalendarVisible = !!this.shortcutOptions.find(shortcut => shortcut.value === 'custom').selected;
      }
      this.isPopoverVisible = true;
    },
    applyPopperStyle(data) {
      if (!this.fullWidth) {
        data.styles.minWidth = Math.max(this.minMenuWidth, data.offsets.reference.width);
      }
      setStyles(data.instance.popper, data.styles);
      setAttributes(data.instance.popper, data.attributes);
      return data;
    },
    flipPopperVariations(data) {
      const {
        offsets
      } = data;
      const {
        popper,
        reference
      } = offsets;
      if (this.type === 'range' && this.columns > 1 && this.visibleColumnCount === 1 && this.twoColumnBreakpoint < window.innerWidth) {
        this.twoColumnBreakpoint = null;
        this.visibleColumnCount++;
        return data;
      }
      const elementLocation = this.$el.getBoundingClientRect();
      const elementLocationBottom = elementLocation.bottom;
      let placement = 'bottom';
      if (elementLocationBottom + this.maxMenuHeight > window.innerHeight) {
        placement = 'top';
      }
      if (reference.left + popper.width < window.innerWidth) {
        placement += '-start';
        data.instance.options.placement = placement;
        return data;
      }
      placement += '-end';
      data.instance.options.placement = placement;
      if (this.type === 'range' && this.visibleColumnCount > 1) {
        this.twoColumnBreakpoint = reference.left + popper.width;
        this.visibleColumnCount--;
        return data;
      }
      data.instance.scheduleUpdate();
      return data;
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-date-picker.vue2.js.map
